import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageBreadcrumb from '../../../../shared/PageBreadCrumb';
import BrainChatPanel, { type BrainLine } from '../../../brain/BrainChatPanel';
import AIArchitectureCanvas from './AIArchitectureCanvas';
import {
  sendBrainMessage,
  startBrainSession,
  type BrainCollectedState,
} from '../../../../services/brainService';

export default function AIProjectArchitect() {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [lines, setLines] = useState<BrainLine[]>([]);
  const [collected, setCollected] = useState<BrainCollectedState>({});
  const [busy, setBusy] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await startBrainSession({ skillName: 'architectProject' });
        if (cancelled) return;
        setSessionId(s.sessionId);
        setCollected(s.collectedState || {});
        setLines([{ role: 'assistant', content: s.greeting }]);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Could not start AI session';
        if (!cancelled) setInitError(msg);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onSend = useCallback(
    async (text: string) => {
      if (!sessionId) {
        toast.error('Session not ready');
        return;
      }
      setLines((prev) => [...prev, { role: 'user', content: text }]);
      setBusy(true);
      try {
        const out = await sendBrainMessage(sessionId, text);
        setCollected(out.collectedState || {});
        setLines((prev) => [...prev, { role: 'assistant', content: out.assistantMessage || '_(no response)_' }]);
        if (out.sessionCompleted && out.collectedState?.projectId) {
          const pid = out.collectedState.projectId;
          toast.success('Project architecture saved.');
          navigate(`/projects/projectManagment?selectedProject=${pid}&showAssetEquipment=true`);
        }
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Request failed';
        toast.error(msg);
        setLines((prev) => [...prev, { role: 'assistant', content: `**Error:** ${msg}` }]);
      } finally {
        setBusy(false);
      }
    },
    [sessionId, navigate]
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-4 md:p-6">
      <PageBreadcrumb pageTitle="AI Project Architect" />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-2xl text-sm text-slate-600">
          Chat with the architect to create a project and solar equipment hierarchy. Ensure{' '}
          <strong>Ollama</strong> is running on the server with <code className="rounded bg-slate-100 px-1">OLLAMA_MODEL</code>{' '}
          pulled (e.g. <code className="rounded bg-slate-100 px-1">gemma3:4b</code>).
        </p>
        <Link
          to="/projects/create"
          className="shrink-0 text-xs sm:text-sm font-medium text-amber-800 underline-offset-2 hover:underline"
        >
          Create project (classic form)
        </Link>
      </div>

      {initError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{initError}</div>
      )}

      <div className="grid min-h-[calc(100vh-220px)] flex-1 grid-cols-1 gap-4 lg:grid-cols-2">
        <BrainChatPanel
          title="AI Project Architect"
          subtitle="Running via unified AI Brain skill: architectProject"
          lines={lines}
          onSend={onSend}
          busy={busy || !sessionId}
          collectedState={collected}
        />
        <AIArchitectureCanvas state={collected} />
      </div>
    </div>
  );
}
